import React from 'react';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';
import { Empty, Spin } from 'antd';
import { ReuniaoPorStatus } from '../../../../types/dashboard/cppd.types';

interface ReunioesChartProps {
  data: ReuniaoPorStatus[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

const CPPDReunioesChart: React.FC<ReunioesChartProps> = ({ 
  data, 
  loading = false, 
  error = null,
  height = 300
}) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin tip="Carregando..." />
      </div>
    );
  }

  if (error) {
    return <Empty description={error} />;
  }

  if (!data || data.length === 0) {
    return <Empty description="Nenhum dado disponível" />;
  }

  // Configuração de cores para cada status
  const COLORS: Record<string, string> = {
    'Pendente': '#faad14',
    'Em andamento': '#1890ff',
    'Concluído': '#52c41a'
  };

  // Verificar se há dados com valores maiores que zero
  const hasData = data.some(item => item.count > 0);
  if (!hasData) {
    return <Empty description="Nenhuma reunião encontrada" />;
  }

  // Calcular total para percentuais
  const total = data.reduce((sum, item) => sum + item.count, 0);

  // Preparar dados para o formato do Recharts
  const chartData = data.map(item => ({
    name: item.status,
    value: item.count,
    percentual: total > 0 ? (item.count / total) * 100 : 0
  }));

  // Filtrar apenas dados com valores maiores que zero
  const filteredData = chartData.filter(item => item.value > 0);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={filteredData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          outerRadius={Math.min(height * 0.3, 80)}
          fill="#8884d8"
          dataKey="value"
          paddingAngle={2}
          animationDuration={800}
          animationBegin={0}
        >
          {filteredData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={COLORS[entry.name as keyof typeof COLORS] || '#d9d9d9'}
              stroke="#fff"
              strokeWidth={1}
            />
          ))}
        </Pie>
        <Tooltip 
          formatter={(value: any, name: any) => {
            const item = filteredData.find(d => d.name === name);
            const percentual = item ? item.percentual.toFixed(1) : '0.0';
            return [`${value} reuniões (${percentual}%)`, name];
          }}
          labelFormatter={() => 'Distribuição por Status'}
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        />
        <Legend 
          layout="horizontal" 
          verticalAlign="bottom" 
          align="center"
          formatter={(value: any) => {
            const item = filteredData.find(d => d.name === value);
            const count = item ? item.value : 0;
            const percentual = item ? item.percentual.toFixed(1) : '0.0';
            return (
              <span style={{ fontSize: '12px', color: '#666' }}>
                {value} ({count} - {percentual}%)
              </span>
            );
          }}
          wrapperStyle={{
            paddingTop: '10px',
            fontSize: '12px'
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default CPPDReunioesChart;